/**
 * Local player preferences.
 *
 * Client-side only, on purpose: how loud the game is and how fast the mouse
 * lays the guns are not facts about the match, and a preference that travelled
 * with the account would be one more thing to get wrong at join time.
 * `localStorage` survives a reload, costs no request, and a shared machine
 * keeps a feel per browser rather than per captain.
 *
 * Volume and mute are two settings, not one. Mute is "not right now" and must
 * not cost the player the level they had chosen; a mute that wrote 0 to the
 * volume would hand them a silent game on the next unmute.
 */
import { ref, watch } from 'vue';

import { prefStorage } from '@/consent/pref-storage';

/** A "preferences" consent item: on disk only once the visitor allows it (consent/pref-storage.ts). */
const STORAGE_KEY = 'admiralty.settings.v1';

export const MIN_AIM_SENSITIVITY = 0.25;
export const MAX_AIM_SENSITIVITY = 3;
export const DEFAULT_AIM_SENSITIVITY = 1;

/** Rounds to the slider's step, so the stored value and the control agree. */
export function clampAimSensitivity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_AIM_SENSITIVITY;
  const v = Math.min(MAX_AIM_SENSITIVITY, Math.max(MIN_AIM_SENSITIVITY, value));
  return Math.round(v * 20) / 20;
}

export function clampVolume(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, Math.round(value * 100) / 100));
}

/**
 * How much the scenes spend on looking good. `auto` reads the pointer: a
 * coarse one (a phone) is `lean`. A real setting rather than a probe alone,
 * because fill rate depends on the display and the GPU in a way no start-up
 * check measures honestly, and a player who wants frames more than foam
 * should be able to say so.
 */
import { DEFAULT_MODE, type MatchMode, isMatchMode } from '@shared/modes';

export type Quality = 'auto' | 'lean' | 'full';
export const QUALITIES: readonly Quality[] = ['auto', 'lean', 'full'];

export interface Settings {
  masterVolume: number;
  /** Music under the master, 0–1. Low by default: the battle is the guns and the sea. */
  musicVolume: number;
  /** Interface and ceremony cues under the master, 0–1 (`audio/sfx.ts`). */
  sfxVolume: number;
  /** The radio voice under the master, 0–1. */
  voiceVolume: number;
  muted: boolean;
  aimSensitivity: number;
  invertPitch: boolean;
  quality: Quality;
  /** Asks the game to hold still, on top of the OS setting: either one is enough (see `engine/motion.ts`). */
  reduceMotion: boolean;
  /** The rules the captain last chose to sail to; what "put to sea" asks for. */
  battleMode: MatchMode;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = {
  masterVolume: 0.7,
  musicVolume: 0.45,
  sfxVolume: 0.8,
  voiceVolume: 0.8,
  muted: false,
  aimSensitivity: DEFAULT_AIM_SENSITIVITY,
  invertPitch: false,
  quality: 'auto',
  reduceMotion: false,
  battleMode: DEFAULT_MODE,
};

/** Read a stored blob back into a valid settings object, whatever is in it. */
export function parseSettings(raw: string | null): Settings {
  const out: Settings = { ...DEFAULT_SETTINGS };
  if (!raw) return out;
  try {
    const v = JSON.parse(raw) as Partial<Record<keyof Settings, unknown>>;
    if (typeof v.masterVolume === 'number') out.masterVolume = clampVolume(v.masterVolume, out.masterVolume);
    if (typeof v.musicVolume === 'number') out.musicVolume = clampVolume(v.musicVolume, out.musicVolume);
    if (typeof v.sfxVolume === 'number') out.sfxVolume = clampVolume(v.sfxVolume, out.sfxVolume);
    if (typeof v.voiceVolume === 'number') out.voiceVolume = clampVolume(v.voiceVolume, out.voiceVolume);
    if (typeof v.muted === 'boolean') out.muted = v.muted;
    if (typeof v.aimSensitivity === 'number') out.aimSensitivity = clampAimSensitivity(v.aimSensitivity);
    if (typeof v.invertPitch === 'boolean') out.invertPitch = v.invertPitch;
    if (v.quality === 'auto' || v.quality === 'lean' || v.quality === 'full') out.quality = v.quality;
    if (typeof v.reduceMotion === 'boolean') out.reduceMotion = v.reduceMotion;
    if (isMatchMode(v.battleMode)) out.battleMode = v.battleMode;
  } catch {
    // Garbage in storage is the same as nothing in storage.
  }
  return out;
}

function load(): Settings {
  try {
    return parseSettings(prefStorage.get(STORAGE_KEY));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

const initial = load();
export const masterVolume = ref(initial.masterVolume);
export const musicVolume = ref(initial.musicVolume);
export const sfxVolume = ref(initial.sfxVolume);
export const voiceVolume = ref(initial.voiceVolume);
export const audioMuted = ref(initial.muted);
export const aimSensitivity = ref(initial.aimSensitivity);
export const invertPitch = ref(initial.invertPitch);
export const quality = ref<Quality>(initial.quality);
export const reduceMotion = ref(initial.reduceMotion);
export const battleMode = ref<MatchMode>(initial.battleMode);

/** The quality a scene should build at now: the setting, or the pointer's answer to `auto`. */
export function resolveQuality(): 'lean' | 'full' {
  if (quality.value !== 'auto') return quality.value;
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  return coarse ? 'lean' : 'full';
}

/** What actually reaches the speakers: 0 while muted, the chosen level otherwise. */
export function currentMasterVolume(): number {
  return audioMuted.value ? 0 : masterVolume.value;
}

watch([masterVolume, musicVolume, sfxVolume, voiceVolume, audioMuted, aimSensitivity, invertPitch, quality, reduceMotion, battleMode], () => {
  const v: Settings = {
    masterVolume: masterVolume.value,
    musicVolume: musicVolume.value,
    sfxVolume: sfxVolume.value,
    voiceVolume: voiceVolume.value,
    muted: audioMuted.value,
    aimSensitivity: aimSensitivity.value,
    invertPitch: invertPitch.value,
    quality: quality.value,
    reduceMotion: reduceMotion.value,
    battleMode: battleMode.value,
  };
  // Memory always, disk only with consent; a blocked store is handled inside.
  prefStorage.set(STORAGE_KEY, JSON.stringify(v));
});
